/**
 * The storefront's glyphs, as inline SVG.
 *
 * Emoji were the previous answer (a 🔍 on the design thumbnail, a 🎁 on the
 * button) and they read as a toy on a shop page: every phone draws them in its
 * own style and none of them match the theme's icons. These are one stroke
 * weight, currentColor, and scale with the text they sit beside.
 */

const svg = (body: string): string =>
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
  body +
  '</svg>';

export const ICONS = {
  /** Four arrows out of the corners: "see it full size". */
  expand: svg('<path d="M15 4h5v5M9 20H4v-5M20 4l-6 6M4 20l6-6"/>'),
  /** A magnifier with a plus: the hover-zoom hint. */
  zoomIn: svg('<circle cx="11" cy="11" r="7"/><path d="M20.5 20.5 16 16M11 8v6M8 11h6"/>'),
  chevronDown: svg('<path d="m6 9 6 6 6-6"/>'),
  chevronLeft: svg('<path d="m15 18-6-6 6-6"/>'),
  chevronRight: svg('<path d="m9 18 6-6-6-6"/>'),
  /** An open eye: "you see it as it will be made". */
  eye: svg('<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>'),
  /** A stack of steps: how many screens the popup has. */
  steps: svg('<path d="M4 6h16M4 12h10M4 18h6"/>'),
  link: svg(
    '<path d="M10 13a5 5 0 0 0 7.1.6l3-3a5 5 0 0 0-7-7l-1.7 1.7"/>' +
      '<path d="M14 11a5 5 0 0 0-7.1-.6l-3 3a5 5 0 0 0 7 7l1.7-1.7"/>',
  ),
  /** A four-point sparkle: "make it yours". */
  sparkle: svg(
    '<path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4z"/>' +
      '<path d="M19 16l.6 1.9 1.9.6-1.9.6-.6 1.9-.6-1.9-1.9-.6 1.9-.6z"/>',
  ),
  check: svg('<path d="M20 6 9 17l-5-5"/>'),
  close: svg('<path d="M18 6 6 18M6 6l12 12"/>'),
  pencil: svg('<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>'),
  /* The sound module's sources. */
  music: svg('<path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>'),
  mic: svg('<rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0M12 17v4M8 21h8"/>'),
  play: svg('<path d="M6 4l14 8-14 8z"/>'),
  film: svg('<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4"/>'),
  folder: svg('<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>'),
  scissors: svg('<circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4 8.1 15.9M14.5 14.5 20 20M8.1 8.1 12 12"/>'),
} as const;

export type IconName = keyof typeof ICONS;

/** A glyph in a span that sizes with the surrounding text (see `.pz-ic`). */
export function iconEl(name: IconName, className = ''): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = className ? `pz-ic ${className}` : 'pz-ic';
  span.innerHTML = ICONS[name];
  span.setAttribute('aria-hidden', 'true');
  return span;
}
